import type { OutputFormat } from './adapters.model';

/** Normalize bracket notation into dot segments (e.g. `a[0].b` → `a.0.b`). */
export function getByPath(obj: unknown, path: string): unknown {
    const normalized = path.replace(/\[(\d+)\]/g, '.$1');
    const keys = normalized.split('.').filter(Boolean);
    let cur: unknown = obj;
    for (const k of keys) {
        if (cur === null || typeof cur !== 'object') return undefined;
        cur = (cur as Record<string, unknown>)[k];
    }
    return cur;
}

function escapeXml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function jsonToXml(obj: unknown, rootTag = 'root'): string {
    const inner = (v: unknown, tag: string): string => {
        const safeTag = tag.replace(/[^a-zA-Z0-9_-]/g, '_');
        if (v === null || v === undefined) return `<${safeTag}/>`;
        if (typeof v === 'object' && !Array.isArray(v)) {
            const body = Object.entries(v as Record<string, unknown>)
                .map(([k, val]) => inner(val, k))
                .join('');
            return `<${safeTag}>${body}</${safeTag}>`;
        }
        if (Array.isArray(v)) {
            return v.map((item) => inner(item, safeTag)).join('');
        }
        return `<${safeTag}>${escapeXml(String(v))}</${safeTag}>`;
    };
    return inner(obj, rootTag);
}

export function jsonToCsv(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value !== 'object') return String(value);
    const rows: string[][] = [];
    const flatten = (prefix: string, v: unknown): void => {
        if (v === null || v === undefined) {
            rows.push([prefix, '']);
        } else if (typeof v !== 'object') {
            rows.push([prefix, String(v)]);
        } else if (Array.isArray(v)) {
            v.forEach((item, i) => flatten(`${prefix}[${i}]`, item));
        } else {
            for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
                const next = prefix ? `${prefix}.${k}` : k;
                flatten(next, val);
            }
        }
    };
    flatten('', value);
    const esc = (c: string) => `"${c.replace(/"/g, '""')}"`;
    return ['path,value', ...rows.map((r) => `${esc(r[0])},${esc(r[1])}`)].join('\n');
}

export function formatAdapterOutput(data: unknown, format: OutputFormat): string {
    switch (format) {
        case 'JSON':
        case 'FHIR':
            return JSON.stringify(data, null, 2);
        case 'XML':
            return jsonToXml(data, 'Output');
        case 'CSV':
            return jsonToCsv(data);
        case 'HL7':
            return String(data);
        default:
            return JSON.stringify(data, null, 2);
    }
}
